import { CheckOutlined } from "@ant-design/icons"
import { COLOR } from "@finway-group/shared/lib/consts"
import { PurchaseOrderStatusEnum } from "@finway-group/shared/lib/models"
import { type QueryParamsFilter } from "@finway-group/shared/lib/models/queryParams/queryParams.types"
import { formatCurrencyNumber } from "@finway-group/shared/lib/utils"
import { calculateTotalAmountInvoiced, calculateTotalQuantityReceived } from "@finway-group/shared/lib/utils/purchaseOrder.util"
import { Button, Modal, Table } from "antd"
import { ColumnsType } from "antd/es/table"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Filter } from "~Components/filter/Filter"
import { FilterSection } from "~Components/filter/FilterSection"
import { PurchaseOrderSelectionFilterForm, PurchaseOrderSelectionFilterFormValues } from "~Components/forms/filter/PurchaseOrderSelectionFilterForm"
import { CornerDownRightArrowIcon } from "~Components/icons/CornerDownRightArrowIcon"
import { ExpandableListItemIcon } from "~Components/icons/ExpandableListItemIcon"
import { PurchaseOrderIcon } from "~Components/icons/PurchaseOrderIcon"
import { ModalType } from "~Components/modals/modal.types"
import { SearchInput } from "~Components/searchInput"
import { PurchaseOrderStatusTag } from "~Features/pages/purchaseOrders/components/PurchaseOrderStatusTag"
import { defaultQueryParamsInvoiceLink } from "~Features/pages/purchaseOrders/queries/const"
import { usePurchaseOrdersForLinkingQuery } from "~Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { type PurchaseOrder } from "~Features/pages/purchaseOrders/types/PurchaseOrder.interfaces"
import { useCostCenters } from "~Shared/hooks/costCenter.hooks"
import { useCostCenters2 } from "~Shared/hooks/costCenter2.hooks"
import { useExpenseAccounts } from "~Shared/hooks/expenseAccount.hooks"
import { useFilter } from "~Shared/hooks/useFilter"
import { PurchaseOrderReference } from "~Shared/models/purchaseOrderReference"
import { formatDate } from "~Shared/utils/date.utils"
import { getTaxLabel } from "~Shared/utils/helper.utils"

interface PurchaseOrderSelectionModalProps extends ModalType {
    onSelection: (selectedPurchaseOrders: Array<PurchaseOrderReference>) => void
    defaultSelectedPurchaseOrders?: Array<PurchaseOrderReference>
    excludePurchaseOrderIds: Array<string>
    defaultFilter: QueryParamsFilter
    vendorId?: string
}

const usePOsQuery = ({
    excludePurchaseOrderIds,
    isShowing,
    defaultFilter,
    filterQuery,
}: Pick<PurchaseOrderSelectionModalProps, "excludePurchaseOrderIds" | "isShowing" | "defaultFilter"> & { filterQuery: QueryParamsFilter }) => {
    const [currentPage, setCurrentPage] = useState(defaultQueryParamsInvoiceLink.page)
    const [search, setSearch] = useState("")
    const filter = { ...defaultFilter, ...filterQuery, _id: { nin: excludePurchaseOrderIds } }
    const { data, isLoading } = usePurchaseOrdersForLinkingQuery(
        {
            page: currentPage,
            limit: defaultQueryParamsInvoiceLink.limit,
            filter,
            search,
        },
        { enabled: isShowing },
    )

    return {
        data,
        isLoading,
        setSearch,
        setCurrentPage,
        currentPage,
    }
}

export const PurchaseOrderSelectionModal = ({
    isShowing,
    onCancel,
    onSelection,
    defaultSelectedPurchaseOrders = [],
    excludePurchaseOrderIds,
    defaultFilter,
    vendorId,
}: PurchaseOrderSelectionModalProps) => {
    const { t } = useTranslation()
    const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState<Array<PurchaseOrderReference>>(defaultSelectedPurchaseOrders)
    const [expandedRowKeysSet, setExpandedRowKeysSet] = useState<Set<string>>(new Set())

    const { filters, filterQuery, onFilter, onRemoveFilter, isFilterModalShowing, showFilterModal, hideFilterModal } = useFilter<PurchaseOrderSelectionFilterFormValues>({
        localStorageKey: undefined,
    })

    useEffect(() => {
        if (vendorId && isShowing && !filters.vendor) {
            onFilter({ vendor: vendorId })
        }
    }, [vendorId, isShowing])

    const { data, isLoading, setSearch, currentPage, setCurrentPage } = usePOsQuery({
        excludePurchaseOrderIds,
        isShowing,
        defaultFilter,
        filterQuery,
    })
    const { docs: purchaseOrders, totalDocs } = data || { docs: [], totalDocs: 0 }
    const costCenters = useCostCenters(true)
    const costCenterss2 = useCostCenters2()
    const expenseAccounts = useExpenseAccounts()

    const onSearch = (value: string) => {
        setSearch(value)
        setCurrentPage(defaultQueryParamsInvoiceLink.page)
    }
    const isPOAlreadyLinked = (po: PurchaseOrder) => defaultSelectedPurchaseOrders.some((defaultPo) => po._id === defaultPo._id && defaultPo.alreadyLinked)

    const handleConfirmSelection = () => {
        onSelection(selectedPurchaseOrders)
        onCancel()
    }

    const toggleRowExpansion = (id: string) => {
        setExpandedRowKeysSet((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(id)) {
                newSet.delete(id)
            } else {
                newSet.add(id)
            }
            return newSet
        })
    }

    const togglePurchaseOrderSelection = (purchaseOrder: PurchaseOrder) => {
        setSelectedPurchaseOrders((prev) => {
            const isSelected = prev.some((po) => po._id === purchaseOrder._id)
            if (isSelected) {
                return prev.filter((po) => po._id !== purchaseOrder._id)
            }
            const poRef: PurchaseOrderReference = {
                _id: purchaseOrder._id,
                purchaseOrderNumber: purchaseOrder.purchaseOrderNumber,
                alreadyLinked: false,
            }
            return [...prev, poRef]
        })
    }

    const isPurchaseOrderSelected = (id: string) => selectedPurchaseOrders.some((po) => po._id === id)

    const columns: ColumnsType<PurchaseOrder> = [
        {
            title: t("label:name"),
            key: "name",
            fixed: "left",
            width: 150,
            render: (_, record: PurchaseOrder) => (
                <div className="flex items-start mb-10 items-center" style={{ maxWidth: "200px", overflow: "hidden" }}>
                    <ExpandableListItemIcon className="mr-10" expanded={expandedRowKeysSet.has(record._id)} onClick={() => toggleRowExpansion(record._id)} />
                    <PurchaseOrderIcon className="mr-10" />
                    <div className="flex flex-col min-w-0 flex-1">
                        <strong>{record.purchaseOrderNumber}</strong>
                        <div className="text-gray-600 text-sm truncate">{record.description}</div>
                    </div>
                </div>
            ),
        },
        {
            title: t("label:date"),
            dataIndex: "dateOfIssue",
            key: "dateOfIssue",
            width: 120,
            render: (dateOfIssue: string) => formatDate(new Date(dateOfIssue)),
        },
        {
            title: t("label:vendor"),
            dataIndex: ["vendor", "name"],
            key: "vendor",
            width: 100,
        },
        {
            title: t("label:amount"),
            dataIndex: "totalGrossPrice",
            key: "amount",
            width: 100,
            render: (amount: number, record: PurchaseOrder) => formatCurrencyNumber({ value: amount, currency: record.currency }),
        },
        {
            title: t("label:status"),
            dataIndex: ["state", "currentState"],
            key: "status",
            width: 80,
            render: (status: PurchaseOrderStatusEnum) => <PurchaseOrderStatusTag status={status} />,
        },
        {
            title: "",
            key: "select",
            width: 100,
            align: "center",
            render: (_, record: PurchaseOrder) => (
                <Button
                    disabled={isPOAlreadyLinked(record)}
                    type={isPurchaseOrderSelected(record._id) ? "primary" : "default"}
                    size="small"
                    onClick={() => togglePurchaseOrderSelection(record)}
                >
                    {isPurchaseOrderSelected(record._id) ? t("action:deselect") : t("action:select")}
                </Button>
            ),
        },
    ]
    const expandedRowRender = (record: PurchaseOrder) => (
        <div className="p-0 space-y-2 border-0">
            {record.lineItems.map((item, index) => (
                <div key={item._id} className="bg-white border-0">
                    <div className="flex items-center py-2 px-3">
                        <CornerDownRightArrowIcon className="mr-10" />
                        <div className={`${COLOR.primary} rounded-full px-10 py-5 mr-10 bg-gray-150`}>
                            <span className="border-0 text-center">{String(index + 1).padStart(2, "0")}</span>
                        </div>
                        {item.materialCode && (
                            <div className={`${COLOR.primary} rounded-full px-10 py-5 mr-10 bg-gray-150 border-0 text-center font-bold`}>{item.materialCode}</div>
                        )}
                        <span className={`text-${COLOR.primary}`}>{item.name}</span>
                    </div>

                    <div className="grid grid-cols-5 gap-4 py-2 px-3">
                        <div>
                            <div className={`${COLOR.primary} font-bold`}>{t("label:quantity")}</div>
                            <div className={`text-[${COLOR.text}]`}>
                                {item.quantity} ({item.unitOfMeasure})
                            </div>
                        </div>

                        <div>
                            <div className={`${COLOR.primary} font-bold`}>{t("label:quantity_received")}</div>
                            <div className={`text-[${COLOR.text}]`}>{calculateTotalQuantityReceived(item)}</div>
                        </div>

                        <div>
                            <div className={`${COLOR.primary} font-bold`}>{t("label:total_net_amount")}</div>
                            <div className={`text-[${COLOR.text}]`}>{formatCurrencyNumber({ value: item.totalNetPrice, currency: record.currency })}</div>
                        </div>

                        <div>
                            <div className={`${COLOR.primary} font-bold`}>{t("label:total_gross_amount")}</div>
                            <div className={`text-[${COLOR.text}]`}>{formatCurrencyNumber({ value: item.totalGrossPrice, currency: record.currency })}</div>
                        </div>

                        <div>
                            <div className={`${COLOR.primary} font-bold`}>{t("label:total_amount_invoiced")}</div>
                            <div className={`text-[${COLOR.text}]`}>{formatCurrencyNumber({ value: calculateTotalAmountInvoiced(item), currency: record.currency })}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-5 gap-4 py-2 px-3">
                        <div>
                            <div className={`${COLOR.primary} font-bold`}>{t("label:vat")}</div>
                            <div className={`text-[${COLOR.text}]`}>{getTaxLabel({ taxRate: item?.taxRate?.taxRate })}</div>
                        </div>

                        <div>
                            <div className={`${COLOR.primary} font-bold`}>{t("label:total_vat_amount")}</div>
                            <div className={`text-[${COLOR.text}]`}>{formatCurrencyNumber({ value: item.totalTaxPrice, currency: record.currency })}</div>
                        </div>

                        <div>
                            <div className={`${COLOR.primary} font-bold`}>{t("label:cost_center")}</div>
                            <div className={`text-[${COLOR.text}] truncate`}>{costCenters.find((cc) => cc._id === item.costCenter)?.name}</div>
                        </div>

                        <div>
                            <div className={`${COLOR.primary} font-bold`}>{t("label:cost_center_2")}</div>
                            <div className={`text-[${COLOR.text}] truncate`}>{costCenterss2.find((cc) => cc._id === item.costCenter2)?.name}</div> {/* TODO use cc2 */}
                        </div>

                        <div>
                            <div className={`${COLOR.primary} font-bold`}>{t("label:expense_account")}</div>
                            <div className={`text-[${COLOR.text}] truncate`}>{expenseAccounts.find((ea) => ea._id === item.expenseAccount)?.accountName}</div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
    return (
        <Modal
            title={t("label:purchase_orders_selection_modal.title")}
            open={isShowing}
            onCancel={onCancel}
            maskClosable={false}
            className="purchase-order-selection-modal ant-modal--very-large-fit"
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    {t("action:cancel")}
                </Button>,
                <Button key="confirm" type="primary" onClick={handleConfirmSelection}>
                    <CheckOutlined />
                    {t("action:confirm")}
                </Button>,
            ]}
        >
            <div className="flex justify-end gap-10">
                <SearchInput onSearch={onSearch} />
                <Filter
                    FilterForm={PurchaseOrderSelectionFilterForm}
                    isFilterModalShowing={isFilterModalShowing}
                    showFilterModal={showFilterModal}
                    hideFilterModal={hideFilterModal}
                    filters={filters}
                    onFilter={onFilter}
                />
            </div>

            <FilterSection filters={filters} onRemove={onRemoveFilter} />

            <Table
                className="min-w-800"
                loading={isLoading}
                dataSource={purchaseOrders}
                columns={columns}
                rowKey="_id"
                scroll={{ x: "max-content" }}
                pagination={{
                    position: ["bottomRight"],
                    current: currentPage,
                    pageSize: defaultQueryParamsInvoiceLink.limit,
                    hideOnSinglePage: false,
                    showSizeChanger: false,
                    total: totalDocs,
                }}
                onChange={(pagination) => {
                    setCurrentPage(pagination.current)
                }}
                expandable={{
                    expandedRowRender,
                    rowExpandable: (record) => record.lineItems.length > 0,
                    expandedRowKeys: Array.from(expandedRowKeysSet),
                    onExpand: (expanded, record) => toggleRowExpansion(record._id),
                    showExpandColumn: false,
                }}
            />
        </Modal>
    )
}
